import { IconName, Table, TableActiveColumnFilters, TableHeader, TableProvider } from '@optimus-web/core';
import { ModalWrapper, NewModalBodyWrapper } from '@mews-commander/ui';
import { LocalizedProperty, lt, useUrlSyncedTableState } from '@mews/framework';
import { VoucherOverview, VoucherOverviewFilters } from '@mews-commander/data-access';
import { RouteName, ActionType } from '@mews-commander/enums';
import { useResolveRoute } from '@mews/routing';
import { WithServiceId } from '@mews-commander/types';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getServiceNames } from '@mews-commander/legacy-entities/src/ducks/services';
import { useCommanderSelector } from '@mews-commander/utils';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import { useGetVoucherTableData } from '../queries/useVoucherTableData';
import { columns } from './columns';

export const VouchersOverviewTable = ({ serviceId }: WithServiceId) => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    const resolveRoute = useResolveRoute();

    const { name: enterpriseName } = useCurrentEnterprise();
    const serviceNames = useCommanderSelector((state) => getServiceNames(state, { id: serviceId }));

    const [tableState, setTableState] = useUrlSyncedTableState<VoucherOverview, VoucherOverviewFilters>({
        columns,
        pageSize: 50,
        extraQuery: {
            OwnerId: serviceId,
        },
    });

    const { data: vouchers, isFetching } = useGetVoucherTableData({
        serviceId,
        ...tableState,
    });

    const breadcrumbs: Breadcrumbs = [
        {
            text: enterpriseName,
            url: resolveRoute(RouteName.EnterpriseDetail, { enterpriseId: currentEnterpriseId }),
        },
        {
            text: lt('ServicePlural'),
            url: resolveRoute(RouteName.ServiceIndex, { query: { OwnerId: currentEnterpriseId } }),
        },
        {
            text: serviceNames ? <LocalizedProperty property={serviceNames} /> : lt('Service'),
            url: resolveRoute(RouteName.VisitDetail, { serviceId }),
        },
        { text: lt('VoucherPlural'), active: true },
    ];

    const actions: HeadingAction[] = [
        {
            url: resolveRoute(RouteName.VoucherCreate, { query: { OwnerId: serviceId } }),
            icon: IconName.Add,
            text: lt('CreateVoucher'),
            type: ActionType.Link,
        },
    ];

    return (
        <ModalWrapper>
            <ModalHeader breadcrumbs={breadcrumbs} actions={actions} />
            <NewModalBodyWrapper padded>
                <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                    <TableHeader title={lt('VoucherPlural')} subTitle={vouchers?.totalRowCount || '0'} />
                    <TableActiveColumnFilters />
                    <Table
                        uniqueRowId="id"
                        data={vouchers?.data ?? []}
                        amountOfRows={vouchers?.totalRowCount ?? 0}
                        loading={isFetching}
                    />
                </TableProvider>
            </NewModalBodyWrapper>
        </ModalWrapper>
    );
};
